import { useState, useMemo } from 'react';
import { Search } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, InputAdornment, Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { PageLoader, Pagination } from '../../../shared_elements';
import { ButtonLoader } from '../../../shared_elements/components';
import { handleExport } from '../../../utils/exportFile';
import { getExpiryOfDocumentsApi } from '../apiService';
import EXcel from '../../assets/img/xlsx_icon.svg';
import Csv from '../../assets/img/csv.png';
// import Paginationstat from './Paginationstat'; // Previous custom pagination flow
import { trackActivity } from '../../../utils/mixpanel';
import '../styles/statistic.scss';

const ExpiryDocuments = ({ expiryData = [], pagination = {}, leaseSlug, onPaginationChange }) => {
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'Statistics', page_type: page_type })
    }
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [searchTerm, setSearchTerm] = useState('');
    // Previous client-side pagination state:
    // const [currentPage, setCurrentPage] = useState(1);
    // const [rowsPerPage, setRowsPerPage] = useState(pagination?.per_page || 10);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [isTableLoading, setIsTableLoading] = useState(false);

    const handlePaginationChange = (query) => {
        setIsTableLoading(true);
        Promise.resolve(onPaginationChange?.(query)).then(
            () => setIsTableLoading(false),
            () => setIsTableLoading(false)
        );
    };

    const onExport = (format) => {
        setAnchorEl(null);
        handleExport({
            URL: getExpiryOfDocumentsApi(leaseSlug, { export_format: format }),
            name: `expiry-documents.${format === 'excel' ? 'xlsx' : 'csv'}`,
            setExportLoader: setIsExporting
        });
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const filteredAndSortedData = useMemo(() => {
        const dataList = Array.isArray(expiryData) ? expiryData : [];
        let filtered = dataList.filter(
            (item) =>
                (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.features || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.based_on_fields || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.expiry_date || '').toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (sortConfig.key) {
            filtered = [...filtered].sort((a, b) => {
                const aVal = a[sortConfig.key] ?? '';
                const bVal = b[sortConfig.key] ?? '';
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return filtered;
    }, [expiryData, searchTerm, sortConfig]);

    /* Previous client-side pagination flow:
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        return filteredAndSortedData.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredAndSortedData, currentPage, rowsPerPage]);

    const paginationInfo = {
        total: pagination?.total ?? filteredAndSortedData.length,
        per_page: rowsPerPage,
        page: currentPage
    };
    */

    return (
        <div>
            <div className="stat-section-header">
                <h3 className="stat-section-title">Expiry of Documents</h3>
                <div className="stat-header-actions">
                    <TextField
                        size="small"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search className="stat-search-icon" />
                                    </InputAdornment>
                                ),
                                className: 'stat-search-input-slot'
                            }
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { height: 32, } }}
                        className="stat-search-field"
                    />
                    <Button
                        disableRipple
                        size='small'
                        variant='outlined'
                        className='btn-primary'
                        onClick={(e) => { setAnchorEl(e.currentTarget); handleItemClicked('Export Button - ExpiryDocuments') }}
                        endIcon={isExporting ? <ButtonLoader size={15} /> : <ArrowDropDownIcon fontSize='small' />}
                    >
                        Export
                    </Button>
                    <Menu elevation={3} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                        <MenuItem onClick={() => { onExport('excel'); handleItemClicked('Export Excel - ExpiryDocuments') }}>
                            <ListItemIcon><img src={EXcel} alt="Excel" width={18} height={18} /></ListItemIcon>
                            <ListItemText primary="Excel" />
                        </MenuItem>
                        <MenuItem onClick={() => { onExport('csv'); handleItemClicked('Export CSV - ExpiryDocuments') }}>
                            <ListItemIcon><img src={Csv} alt="CSV" width={18} height={18} /></ListItemIcon>
                            <ListItemText primary="CSV" />
                        </MenuItem>
                    </Menu>
                </div>
            </div>

            <div className="stat-table-wrapper">
            <TableContainer className="stat-table-container stat-table-container--max-333 lease-detail-table">
                <Table className="mui-table-format" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell onClick={() => { handleSort('name'); handleItemClicked('Table Sorting: Name - ExpiryDocuments') }} className="stat-cell--clickable" style={{ width: '25%' }}>Name</TableCell>
                            <TableCell onClick={() => { handleSort('features'); handleItemClicked('Table Sorting: Features - ExpiryDocuments') }} className="stat-cell--clickable">Features</TableCell>
                            <TableCell onClick={() => { handleSort('based_on_fields'); handleItemClicked('Table Sorting: Based On Fields - ExpiryDocuments') }} className="stat-cell--clickable">Based On Fields</TableCell>
                            <TableCell onClick={() => { handleSort('expiry_date'); handleItemClicked('Table Sorting: Expiry Date - ExpiryDocuments') }} className="stat-cell--clickable">Expiry Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredAndSortedData.length ? filteredAndSortedData.map((row, idx) => (
                            <TableRow key={row.id ?? idx}>
                                <TableCell>{row.name || '-'}</TableCell>
                                <TableCell>{row.features || '-'}</TableCell>
                                <TableCell>{row.based_on_fields || '-'}</TableCell>
                                <TableCell>{row.expiry_date || '-'}</TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={4} className="stat-empty-cell">No documents available</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {isTableLoading ? <PageLoader /> : null}
            </div>
            <Paper>
                {/* Previous custom pagination flow:
                <Paginationstat
                    pagination={paginationInfo}
                    onPageChange={(_, newPage) => setCurrentPage(newPage + 1)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setCurrentPage(1); }}
                    stacks={[10, 20, 50]}
                />
                */}
                <Pagination
                    pagination={pagination} onPageChange={(_, newPage) => handlePaginationChange({ page: newPage + 1, per_page: pagination?.per_page || 10, })} onRowsPerPageChange={(event) => handlePaginationChange({ page: 1, per_page: parseInt(event.target.value, 10), })}
                />
            </Paper>
        </div>
    );
};

export default ExpiryDocuments;
